import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { cn } from '../../lib/utils';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const stages = [
  { id: 'new', label: 'New Users', color: 'bg-gray-500' },
  { id: 'engaged', label: 'Engaged', color: 'bg-orange-500' },
  { id: 'started', label: 'Started Resume', color: 'bg-yellow-500' },
  { id: 'saved', label: 'Saved Resume', color: 'bg-purple-500' },
  { id: 'paid', label: 'Paid & Downloaded', color: 'bg-emerald-500' }
];

const UserFunnelChart = ({ data }) => {
  console.log('🔍 UserFunnelChart received data:', data);

  if (!data) {
    return (
      <Card className="relative overflow-hidden bg-card hover:bg-card/90 transition-all duration-300 border border-border rounded-xl shadow-sm hover:shadow-md">
        <CardHeader>
          <CardTitle className="text-lg font-medium text-foreground">
            User Journey Funnel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No data available</p>
        </CardContent>
      </Card>
    );
  }

  const totalUsers = data.new || 1; // Use new users as baseline, prevent division by 0
  const getPercentage = (value) => ((value / totalUsers) * 100).toFixed(1);

  return (
    <Card className="relative overflow-hidden bg-card hover:bg-card/90 transition-all duration-300 border border-border rounded-xl shadow-sm hover:shadow-md">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.02] via-primary/[0.01] to-transparent pointer-events-none" />
      <CardHeader>
        <CardTitle className="text-lg font-medium text-foreground">
          User Journey Funnel
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {stages.map((stage, index) => {
          if (!stage) return null; // Skip if stage is undefined
          const value = data[stage.id] || 0;
          const percentage = getPercentage(value);
          const previousValue = index > 0 && stages[index - 1] ? data[stages[index - 1].id] || 0 : 0;
          const dropoff = index > 0 && previousValue > 0 ? getPercentage(previousValue - value) : 0;

          return (
            <motion.div
              key={stage.id}
              className="space-y-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={cn("w-2 h-2 rounded-full", stage.color)} />
                  <span className="text-sm font-medium text-foreground">{stage.label}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium tabular-nums text-foreground">{value.toLocaleString()}</span>
                  <span className="text-sm text-muted-foreground">{percentage}%</span>
                  {index > 0 && (
                    <span className="text-sm text-red-500 font-medium">
                      -{dropoff}% dropoff
                    </span>
                  )}
                </div>
              </div>
              <div className="relative">
                <Progress
                  value={percentage}
                  className="h-2 bg-muted/50"
                  indicatorClassName={cn(
                    "transition-all duration-500 ease-out",
                    stage.color
                  )}
                />
              </div>
              {index < stages.length - 1 && (
                <div className="flex justify-center">
                  <ChevronDown className="h-4 w-4 text-muted-foreground/50" />
                </div>
              )}
            </motion.div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default UserFunnelChart; 